import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { categories, categoryTrailForProductType } from "@/lib/categories";
import { UNCATEGORISED_CRUMB } from "../../scripts/category-trail.mjs";
import { BeforeYouBuy } from "@/components/pdp/BeforeYouBuy";
import { DiscontinuedAlternatives } from "@/components/pdp/DiscontinuedAlternatives";
import { getShopifyImageUrl, fetchProductByHandle, fetchProducts, formatPrice, ShopifyProduct } from "@/lib/shopify";
import { ProductJsonLd, BreadcrumbJsonLd } from "@/components/JsonLd";
import { OGTags } from "@/components/OGTags";
import { isDelisted } from "@/lib/delisted";
import { useProductHeadTitle, productHeadDescription, useCanonicalHandle, productPageName } from "@/lib/productMeta";
import { useRecentlyViewedStore } from "@/stores/recentlyViewedStore";
import { useProductRating } from "@/hooks/useProductRating";
import { useCartStore } from "@/stores/cartStore";
import { trackProductView } from "@/lib/analytics";
import { getEditorialSource, saveEditorialOutcome } from '@/lib/editorialAttribution';
import { saveEmailOutcome } from '@/lib/emailAttribution';
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ProductCard } from "@/components/ProductCard";
import { ProductReviews } from "@/components/ProductReviews";
import { ProductQuestions } from "@/components/pdp/ProductQuestions";
import { WhyBuyFromUs } from "@/components/WhyBuyFromUs";
import { BundleSection } from "@/components/pdp/BundleSection";
import { ProductDetails } from "@/components/pdp/ProductDetails";
import { DimensionsSection } from "@/components/pdp/DimensionsSection";
import { RecentlyViewedSection } from "@/components/pdp/RecentlyViewedSection";
import { AutoRotateGallery } from "@/components/pdp/AutoRotateGallery";
import { SizeGuide } from "@/components/pdp/SizeGuide";
import { FitFacts } from "@/components/pdp/FitFacts";
import { ImageGallery } from "@/components/pdp/ImageGallery";
import { ProductInfoPanel } from "@/components/pdp/ProductInfoPanel";
import { StickyAddToCart } from "@/components/pdp/StickyAddToCart";
import { cleanProductTitle, cleanProductDescription, dedupeProducts, uniqueDisplayNames } from "@/lib/productTitle";
import { isLargeFurniture } from "@/lib/productSize";
import { defaultVariant, openingPhoto, sameImageUrl, sizeFactsApply, variantLabel } from "@/lib/variants";
import { useStickyBuyBar } from "@/stores/stickyBuyBarStore";
import { complementTypes } from "@/lib/complements";
import { exactTypeClause, keepExactTypes } from "@/lib/productTypes";
import { correctedType } from "../../scripts/type-overrides.mjs";

import { Button } from "@/components/ui/button";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
const ProductDetail = () => {
  const { handle } = useParams<{ handle: string }>();
  const [product, setProduct] = useState<ShopifyProduct['node'] | null>(null);
  const [loading, setLoading] = useState(true);
  // The Storefront API did not answer (as opposed to answering "no such product").
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState<string | null>(null);
  // Whether the shopper has chosen a size or colour here, or the page chose.
  const [variantPicked, setVariantPicked] = useState(false);
  const [recommendations, setRecommendations] = useState<ShopifyProduct[]>([]);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const addToRecentlyViewed = useRecentlyViewedStore(state => state.add);
  const isCartLoading = useCartStore(state => state.isLoading);
  const numericId = product?.id?.replace(/^gid:\/\/shopify\/Product\//, "") || "";
  const { rating: productRating, count: reviewCount } = useProductRating(numericId, handle || "");
  const canonicalHandle = useCanonicalHandle(handle);
  // Head title matches the prerendered <title>/<h1>, qualifier included.
  const headTitle = useProductHeadTitle(handle, product?.title, product?.productType);
  const pageName = productPageName(headTitle);
  // "You Might Also Like" is capped at 8 so a high-ticket item isn't buried in competitors.
  const railProducts = useMemo(() => recommendations.slice(0, 8), [recommendations]);
  const railNames = useMemo(() => uniqueDisplayNames(railProducts), [railProducts]);


  useEffect(() => {
    // The page renders as soon as the product itself arrives; the recommendation
    // queries (20 + 15 products) follow in the background. They used to hold the
    // whole page, gallery included, behind a second round trip.
    let cancelled = false;
    const loadProduct = async () => {
      if (!handle) return;
      setLoading(true);
      setQuantity(1);
      setSelectedVariant(null);
      setVariantPicked(false);
      setRecommendations([]);
      setLoadFailed(false);
      let data: ShopifyProduct['node'] | null = null;
      try {
        // A failed request is not a deleted product: retry twice before giving up.
        for (let attempt = 0; ; attempt++) {
          try {
            data = await fetchProductByHandle(handle);
            break;
          } catch (err) {
            if (attempt >= 2) throw err;
            await new Promise((r) => setTimeout(r, 700 * (attempt + 1)));
            if (cancelled) return;
          }
        }
        if (cancelled) return;
        setProduct(data);
        // Open on a variant that can be bought: the first one is often sold out
        // while others are in stock. A link to one size (?variant=<id>,
        // Shopify's form; the tree size calculator's picks) opens on it while
        // it is in stock, as if the shopper had picked it.
        const wanted = new URLSearchParams(window.location.search).get("variant");
        const linked =
          wanted && /^\d+$/.test(wanted)
            ? data?.variants?.edges?.find((e) => e.node.availableForSale && e.node.id.endsWith(`/${wanted}`))?.node
            : undefined;
        const initial = linked ?? (data ? defaultVariant(data) : undefined);
        if (initial) setSelectedVariant(initial.id);
        if (linked) setVariantPicked(true);
        addToRecentlyViewed(handle);
        // Track product view analytics
        if (data) {
          void saveEmailOutcome('product_view', data.handle).catch(() => undefined);
          const editorial = getEditorialSource();
          if (editorial) {
            saveEditorialOutcome('product_view', editorial, data.handle);
            window.__lovableEvents?.track?.('project.editorial_product_view', { source_kind: editorial.kind, source_slug: editorial.slug, product_handle: data.handle });
          }
          const price = parseFloat(initial?.price.amount || data.priceRange?.minVariantPrice?.amount || "0");
          window.__lovableEvents?.track?.('project.product_viewed', {
            product_id: data.id,
            category: data.productType || 'Uncategorized',
            value: price,
            currency: initial?.price.currencyCode || data.priceRange?.minVariantPrice?.currencyCode || 'USD',
          });
          trackProductView({
            id: data.id,
            name: data.title,
            price,
            currency: data.priceRange?.minVariantPrice?.currencyCode || "USD",
          });
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setProduct(null);
          setLoadFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
      // A missing product shows the "no longer available" page, which has no rail.
      if (cancelled || !data) return;
      try {
        // Fetch recommendations: COMPLEMENTARY items, not same type
        const productType = data.productType;
        let recs: ShopifyProduct[] = [];
        if (productType) {
          // Determine complementary product types based on the current product
          // Exact catalogue types from the shared pairing table: the old map
          // asked for "End Tables", "Bar Stools", "Sideboards & Buffets" and
          // others that don't exist, so those slots came back empty. One small
          // request per type keeps the rail mixed.
          const complementaryTypes = complementTypes(data.title, productType).slice(0, 4);
          if (complementaryTypes.length > 0) {
            // Each type exactly: `product_type:"Benches"` also returns Shower
            // Benches, which a bed page then offered as its bench.
            const perType = await Promise.all(
              complementaryTypes.map((t) => fetchProducts(6, exactTypeClause(t)).then((list) => keepExactTypes(list, [t]))),
            );
            const interleaved: ShopifyProduct[] = [];
            for (let i = 0; i < 6; i++) for (const list of perType) if (list[i]) interleaved.push(list[i]);
            recs = interleaved.filter(p => p.node.handle !== handle);
          }
          // If not enough complementary items, fetch same type as fallback
          if (recs.length < 4) {
            const sameType = keepExactTypes(await fetchProducts(15, exactTypeClause(productType)), [productType]);
            const existingIds = new Set(recs.map(r => r.node.id));
            recs = [...recs, ...sameType.filter(p => p.node.handle !== handle && !existingIds.has(p.node.id))];
          }
        } else {
          const fallback = await fetchProducts(15);
          recs = fallback.filter(p => p.node.handle !== handle);
        }
        if (!cancelled) setRecommendations(dedupeProducts(recs.filter((p) => p.node.handle !== handle)).slice(0, 12));
      } catch (err) {
        console.error(err);
      }
    };
    loadProduct();
    return () => {
      cancelled = true;
    };
  }, [handle, reloadKey]);

  // The page opens on the photo the head preloads (openingPhoto: the titled
  // variant's own when the variants differ in look and it shows the whole
  // piece, else the lead photo), and shows the selected variant's own photo
  // once the shopper picks one. Only when there is a choice to make: a lone
  // variant's image is not always the product's lead photo.
  const variantEdges = product?.variants?.edges || [];
  const opening = useMemo(() => (product ? openingPhoto(product) : undefined), [product]);
  const variantImage =
    (variantPicked
      ? variantEdges.length > 1 && variantEdges.find((v) => v.node.id === selectedVariant)?.node.image
      : opening) || null;
  const pickVariant = (id: string) => {
    setVariantPicked(true);
    setSelectedVariant(id);
  };
  // The product query reads ten images; a variant photo beyond them is put
  // first. One already in the list is shown at its own URL, the one the head
  // preloaded: a re-uploaded copy of a file differs in its upload id.
  const galleryImages = useMemo(() => {
    const all = product?.images?.edges || [];
    if (!variantImage?.url) return all;
    const at = all.findIndex((i) => sameImageUrl(i.node.url, variantImage.url));
    if (at < 0) return [{ node: variantImage }, ...all];
    return all.map((i, n) => (n === at ? { node: { ...i.node, url: variantImage.url } } : i));
  }, [product, variantImage]);

  useEffect(() => {
    const onScroll = () => setShowStickyBar(window.scrollY > 500);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // The desktop buy bar below; the mobile one is StickyAddToCart.
  const setStickyBar = useStickyBuyBar((s) => s.set);
  // Only for a selection that can be bought: a sold-out one kept a greyed-out
  // ADD TO CART bar on screen under the waitlist form.
  const desktopBarVisible =
    showStickyBar && !loading && !!product?.variants?.edges?.find((v) => v.node.id === selectedVariant)?.node.availableForSale;
  useEffect(() => {
    setStickyBar("desktop", desktopBarVisible);
    return () => setStickyBar("desktop", false);
  }, [desktopBarVisible, setStickyBar]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        <Footer />
      </div>
    );
  }

  if (loadFailed) {
    // No OGTags on purpose: the served title, description, canonical and price
    // tags stay (prerenderedHead only removes a tag Helmet replaces), and the
    // page is not marked noindex because the request failed, not the product.
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center py-16">
          <div className="text-center max-w-md px-6">
            <h1 className="font-display text-2xl font-semibold mb-4">We couldn't load this product</h1>
            <p className="text-[14px] text-muted-foreground mb-6">The connection to our catalogue dropped. Please try again.</p>
            <Button
              onClick={() => setReloadKey((k) => k + 1)}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full text-[13px] tracking-widest uppercase px-8 py-5"
            >
              Try again
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!product) {
    // The supplier deletes and renames products daily, so a handle that was live
    // at build time can disappear. Without noindex these URLs keep the homepage's
    // title and canonical, and Google files them as soft 404s / homepage duplicates.
    return (
      <div className="min-h-screen flex flex-col">
        <OGTags
          title="Product no longer available | LoftLoom"
          description="This product is no longer available. Browse the current collection instead."
          noindex
        />
        <Header />
        <div className="flex-1 flex flex-col">
          <div className="flex items-center justify-center py-16">
            <div className="text-center max-w-md px-6">
              <h1 className="font-display text-2xl font-semibold mb-4">This product is no longer available</h1>
              <p className="text-[14px] text-muted-foreground mb-6">
                It has been discontinued or replaced since you last saw it. Similar pieces that are in
                stock now are below, or browse the current collection by category.
              </p>
              <Link to="/categories"><Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full text-[13px] tracking-widest uppercase px-8 py-5">Browse Categories</Button></Link>
            </div>
          </div>
          {handle && <DiscontinuedAlternatives handle={handle} />}
        </div>
        <Footer />
      </div>
    );
  }

  const images = product.images?.edges || [];
  const currentVariant = product.variants?.edges?.find(v => v.node.id === selectedVariant)?.node;

  // The supplier's measurements describe one size; shown only for that one.
  const sizeFactsOk = sizeFactsApply(product, currentVariant);
  const selectedLabel = variantLabel(product, currentVariant);

  // The desktop bar clicks the real ADD TO CART button, as the phone bar does,
  // so quantity, result handling and the "Added to cart" toast are the same.
  const handleAddToCart = () => {
    (document.querySelector("[data-pdp-add-to-cart]") as HTMLButtonElement | null)?.click();
  };

  // Head and Product JSON-LD exactly as prerendered: the generated description,
  // the photo the page opens on at 1200 px, and no schema on a duplicate that
  // canonicalises to another page.
  const headDescription = productHeadDescription({
    title: product.title,
    description: cleanProductDescription(product.description) || product.description,
    productType: product.productType,
    price: Number(currentVariant?.price.amount ?? product.priceRange?.minVariantPrice?.amount ?? 0),
    availableForSale: currentVariant?.availableForSale ?? product.availableForSale,
  });
  const shareSource = opening?.url ?? images[0]?.node.url;
  const shareImage = shareSource ? getShopifyImageUrl(shareSource, 1200) : undefined;
  const selfCanonical = (canonicalHandle ?? product.handle) === product.handle;

  // Room first, most specific category last. One rule with the prerendered page
  // (scripts/category-trail.mjs), so both breadcrumbs name the same path.
  // The supplier files a few products under a type they are not (a bookcase as Loft Beds).
  const categoryTrail = categoryTrailForProductType(correctedType(product.title, product.productType));
  const categoryCrumbs = categoryTrail.length
    ? categoryTrail.map((c) => ({ label: c.name, href: `/category/${c.slug}` }))
    : [UNCATEGORISED_CRUMB];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 pb-[7.5rem] md:pb-0">
        <OGTags
          noindex={isDelisted(product.title)}
          title={headTitle}
          description={headDescription}
          image={shareImage}
          url={`https://loftloom.com/product/${product.handle}`}
          // Duplicate clusters canonicalise to their representative; everything
          // else stays self-canonical. Matches the prerendered head exactly.
          canonical={`https://loftloom.com/product/${canonicalHandle ?? product.handle}`}
          type="product"
        />
        {selfCanonical && (
          <ProductJsonLd
            name={pageName}
            description={headDescription}
            image={shareImage}
            price={Number(currentVariant?.price.amount ?? 0).toFixed(2)}
            currency={currentVariant?.price.currencyCode || "USD"}
            availability={currentVariant?.availableForSale || false}
            url={`https://loftloom.com/product/${product.handle}`}
            ratingValue={productRating || undefined}
            reviewCount={reviewCount || undefined}
          />
        )}
        <Breadcrumbs items={[...categoryCrumbs, { label: pageName }]} />
        {selfCanonical && (
          <BreadcrumbJsonLd
            items={[
              { name: "Home", url: "https://loftloom.com/" },
              ...categoryCrumbs.map((c) => ({ name: c.label, url: `https://loftloom.com${c.href}` })),
              { name: pageName, url: `https://loftloom.com/product/${product.handle}` },
            ]}
          />
        )}

        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4 md:py-8">
        {/* minmax(0, …) tracks: an auto track grows to its widest no-wrap child
            (truncated rows, the gallery strip) and pushed the buy box off-screen on phones. */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr),420px] gap-6 lg:gap-12 items-start">
            <div className="min-w-0"><ImageGallery images={galleryImages} activeImageUrl={variantImage?.url} title={cleanProductTitle(product.title, product.productType)} /></div>
            <div className="min-w-0 lg:sticky lg:top-[76px] lg:max-h-[calc(100vh-92px)] lg:overflow-y-auto lg:scrollbar-hide">
              <ProductInfoPanel
                product={product}
                pageName={pageName}
                selectedVariant={selectedVariant}
                setSelectedVariant={pickVariant}
                quantity={quantity}
                setQuantity={setQuantity}
                recommendations={recommendations}
                sizeFactsApply={sizeFactsOk}
              />
            </div>
          </div>
        </div>

        <StickyAddToCart product={product} selectedVariant={selectedVariant} image={variantImage?.url ?? images[0]?.node.url} />

        {/* Below the fold */}
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.6 }}>
          <ProductDetails title={product.title} description={cleanProductDescription(product.description) || ""} images={images} variantTitle={currentVariant?.title} productType={product.productType} showAssembly={isLargeFurniture(product.productType, product.title)} sizeFactsApply={sizeFactsOk} />
        </motion.div>
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.6 }}>
          {/* Spec sections parse raw feed text — cleaning strips the spec block they read. */}
          <DimensionsSection description={product.description || ""} title={product.title} variantTitle={currentVariant?.title} productType={product.productType} sizeFactsApply={sizeFactsOk} />
        </motion.div>
        {/* Both work from the title's size and the supplier's one set of measurements. */}
        {sizeFactsOk && <FitFacts description={product.description || ""} title={product.title} productType={product.productType} />}
        {/* AutoRotateGallery removed */}
        {/* Room-fit calculator is only meaningful for large furniture. */}
        {sizeFactsOk && isLargeFurniture(product.productType, product.title) && (
          <SizeGuide description={product.description || ""} title={product.title} />
        )}

        {/* Journal guides for this product: its product type's own guides, then its category's. */}
        {(() => {
          const pt = product.productType || "";
          const matches = (c: typeof categories[number]) =>
            !!c.query && c.query.split(" OR ").some((q) => {
              const match = q.match(/product_type:"?([^"]+)"?/i);
              return match && match[1].toLowerCase() === pt.toLowerCase();
            });
          // The most specific subcategory — the one listing the fewest types — so a
          // vanity reads Bathroom Vanities' guides, not all of Bathroom's. Room-level
          // categories are left out: their guides are about the room's other pieces.
          const terms = (c: typeof categories[number]) => c.query!.split(" OR ").length;
          const cat = pt
            ? categories.filter((c) => c.parent && matches(c)).sort((a, b) => terms(a) - terms(b))[0]
            : undefined;
          return <BeforeYouBuy productType={pt} categorySlug={cat?.slug} />;
        })()}




        {/* You Might Also Like */}
        {recommendations.length > 0 && (
          <motion.section initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }} className="border-t border-border py-10 md:py-14">
            <div className="max-w-[1400px] mx-auto px-4 md:px-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-sans text-[22px] md:text-[26px] font-semibold text-foreground">You Might Also Like</h2>
                <div className="hidden md:flex gap-2">
                  <button aria-label="Previous recommendations" onClick={() => document.getElementById('recs-carousel')?.scrollBy({ left: -280, behavior: 'smooth' })} className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:border-foreground/40 transition-colors">
                    <ChevronLeft className="h-4 w-4 text-foreground" />
                  </button>
                  <button aria-label="Next recommendations" onClick={() => document.getElementById('recs-carousel')?.scrollBy({ left: 280, behavior: 'smooth' })} className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:border-foreground/40 transition-colors">
                    <ChevronRight className="h-4 w-4 text-foreground" />
                  </button>
                </div>
              </div>
              <div id="recs-carousel" className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                {railProducts.map((p) => (
                  <div key={p.node.id} className="flex-shrink-0 w-[200px] md:w-[220px]">
                    <ProductCard product={p} displayName={railNames[p.node.handle]} />
                  </div>
                ))}
              </div>
            </div>
          </motion.section>
        )}

        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }}>
          {recommendations.length > 2 && <BundleSection currentProduct={product} currentVariant={currentVariant} recommendations={recommendations} />}
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }}>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }}>
          <div id="pdp-reviews"><ProductReviews productTitle={product.title} productId={product.id} productHandle={product.handle} productImages={images.slice(0, 4).map(img => img.node.url)} /></div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }}>
          <div id="product-questions" className="scroll-mt-24" />
          <ProductQuestions productHandle={product.handle} productId={product.id} productTitle={cleanProductTitle(product.title, product.productType)} />
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }}>
          {handle && <RecentlyViewedSection currentHandle={handle} />}
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }}>
          <WhyBuyFromUs />
        </motion.div>
      </main>

      {/* Sticky Add to Cart Bar — desktop only; phones get StickyAddToCart. */}
      <AnimatePresence>
        {showStickyBar && currentVariant?.availableForSale && (
          <motion.div
            initial={{ y: 80 }}
            animate={{ y: 0 }}
            exit={{ y: 80 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="hidden md:block fixed bottom-0 left-0 right-0 z-40 bg-background border-t border-border py-3"
          >
            <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {images[0] && (
                  <img src={getShopifyImageUrl(variantImage?.url ?? images[0].node.url, 112)} alt="" className="w-10 h-10 object-contain bg-[#f5f0eb] rounded flex-shrink-0" loading="lazy" decoding="async" />
                )}
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-foreground truncate">{pageName}</p>
                  <p className="text-[14px] text-primary font-semibold">
                    {formatPrice(currentVariant.price.amount, currentVariant.price.currencyCode)}
                    {/* Which size or colour that price is for, as on the phone bar. */}
                    {selectedLabel && <span className="ml-2 text-[12px] font-normal text-muted-foreground">{selectedLabel}</span>}
                  </p>
                </div>
              </div>
              <Button onClick={handleAddToCart} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full text-[12px] tracking-[0.15em] uppercase px-8 h-10 font-bold flex-shrink-0" disabled={!currentVariant.availableForSale || isCartLoading}>
                ADD TO CART
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default ProductDetail;
